import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useFiliera } from "@/context/FilieraContext";
import ChatAssistant from "@/components/ChatAssistant";
import {
  ChartLineUp, Buildings, UsersThree, Truck, Coins, Files, Bank,
  ClipboardText, Receipt, TreeStructure, Robot, SignOut, FileArrowDown, Users, ShieldCheck, Stack, ListMagnifyingGlass
} from "@phosphor-icons/react";

const Item = ({ to, icon: Icon, label, testId }) => (
  <NavLink to={to} end={to === "/"} data-testid={testId}
    className={({ isActive }) => `sb-item ${isActive ? "active" : ""}`}>
    <Icon size={18} weight="duotone" />
    <span>{label}</span>
  </NavLink>
);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { filiere, selectedFilieraId, setSelectedFilieraId, loading, error } = useFiliera();
  const navigate = useNavigate();
  if (!user) return null;

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex-col hidden lg:flex sticky top-0 h-screen">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-800">
          <div className="w-9 h-9 bg-white text-slate-900 rounded flex items-center justify-center">
            <Buildings size={20} weight="duotone" />
          </div>
          <div>
            <div className="font-bold text-sm tracking-tight" style={{ fontFamily: "Chivo" }}>FCF MANAGER</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">Bandi Pubblici</div>
          </div>
        </div>

        <div className="px-3 pt-4 pb-2 border-b border-slate-800">
          <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-2" htmlFor="global-filiera-select">
            Filiera attiva
          </label>
          <select
            id="global-filiera-select"
            data-testid="global-filiera-select"
            value={selectedFilieraId}
            onChange={(e) => setSelectedFilieraId(e.target.value)}
            disabled={loading}
            className="w-full h-10 rounded-md bg-slate-800 border border-slate-700 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
          >
            <option value="">{loading ? "Caricamento…" : "Nessuna filiera selezionata"}</option>
            {filiere.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
          {error && <div className="mt-2 text-[11px] text-red-300">{error}</div>}
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          <div className="sb-section">Analytics</div>
          <Item to="/" icon={ChartLineUp} label="Dashboard" testId="nav-dashboard" />

          <div className="sb-section">Anagrafica</div>
          <Item to="/filiere" icon={TreeStructure} label="Filiere" testId="nav-filiere" />
          <Item to="/beneficiari" icon={UsersThree} label="Beneficiari" testId="nav-beneficiari" />
          <Item to="/investimenti" icon={Coins} label="Investimenti" testId="nav-investimenti" />
          <Item to="/fornitori" icon={Truck} label="Fornitori" testId="nav-fornitori" />

          <div className="sb-section">Rendicontazione</div>
          <Item to="/documenti" icon={ClipboardText} label="Documenti" testId="nav-documenti" />
          <Item to="/bonifici" icon={Bank} label="Bonifici" testId="nav-bonifici" />
          <Item to="/sal" icon={Receipt} label="SAL & Dossier" testId="nav-sal" />
          <Item to="/compliance" icon={ShieldCheck} label="Conformità" testId="nav-compliance" />

          <div className="sb-section">Document Center</div>
          <Item to="/document-center" icon={Files} label="Archivio Documenti" testId="nav-doc-center" />
          <Item to="/upload" icon={Robot} label="Upload & AI OCR" testId="nav-upload" />
          <Item to="/batch-upload" icon={Stack} label="Batch Upload AI" testId="nav-batch-upload" />
          <Item to="/export" icon={FileArrowDown} label="Export" testId="nav-export" />

          {user.role === "admin" && (
            <>
              <div className="sb-section">Amministrazione</div>
              <Item to="/utenti" icon={Users} label="Utenti" testId="nav-utenti" />
              <Item to="/audit-log" icon={ListMagnifyingGlass} label="Audit Log AI" testId="nav-audit-log" />
            </>
          )}
        </nav>

        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 p-2">
            <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest">{user.role}</div>
            </div>
            <button onClick={logout} data-testid="btn-logout" title="Esci"
              className="w-8 h-8 rounded flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition">
              <SignOut size={18} weight="bold" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      <ChatAssistant />
    </div>
  );
}
